import { Injectable, signal, computed } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { HttpClientService } from './http-client.service';

/** Role values as the API sends them. */
export type Role = 'lid' | 'beheerder';

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  role: Role | null;
  customerStatus: string | null;
}

export interface AuthResponse {
  token: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

/** JWT claims as the backend puts them in the token. */
interface TokenPayload {
  id: number;
  username: string;
  firstName?: string;
  lastName?: string;
  createdAt?: string;
  role?: Role;
  customerStatus?: string;
  iat?: number;
  exp?: number;
}

const TOKEN_STORAGE_KEY = 'auth_token';

/** The backend refuses a token after 30 minutes without requests (bundle JwtAuthenticator). */
const INACTIVITY_LIMIT_SECONDS = 30 * 60;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly currentUserSignal = signal<User | null>(null);

  readonly currentUser = this.currentUserSignal.asReadonly();

  readonly isLoggedIn = computed(() => this.currentUserSignal() !== null);

  /** Start page after login: beheer for administrators, the member area for customers. */
  readonly homePath = computed(() => (this.currentUserSignal()?.role === 'beheerder' ? '/beheer' : '/leden'));

  constructor(private http: HttpClientService) {
    this.restoreUserFromStoredToken();
  }

  private restoreUserFromStoredToken(): void {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    if (token && !this.storeToken(token)) {
      // Expired or unreadable: the backend would refuse it anyway
      this.logout();
    }
  }

  /**
   * Stores a new or refreshed token and updates the session from its payload (role and customer status are
   * for display only; the backend checks them itself). Returns false for an unusable or expired token.
   */
  storeToken(token: string): boolean {
    const payload = decodeTokenPayload(token);
    const nowSeconds = Date.now() / 1000;

    if (
      !payload ||
      (payload.exp !== undefined && payload.exp <= nowSeconds) ||
      (payload.iat !== undefined && nowSeconds - payload.iat > INACTIVITY_LIMIT_SECONDS)
    ) {
      return false;
    }

    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    this.currentUserSignal.set({
      id: payload.id,
      email: payload.username,
      firstName: payload.firstName ?? '',
      lastName: payload.lastName ?? '',
      createdAt: payload.createdAt ?? '',
      role: payload.role ?? null,
      customerStatus: payload.customerStatus ?? null,
    });

    return true;
  }

  login(credentials: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/auth/login', credentials).pipe(
      tap(response => this.storeToken(response.token))
    );
  }

  requestPasswordResetLink(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>('/auth/forgot-password', { email });
  }

  setNewPassword(token: string, password: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>('/auth/reset-password', { token, password });
  }

  logout(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    this.currentUserSignal.set(null);
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  }
}

/** Reads the (unverified) payload of a JWT; the segments are base64url and may contain UTF-8 names. */
function decodeTokenPayload(token: string): TokenPayload | null {
  try {
    const segment = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(segment), character => character.charCodeAt(0));

    return JSON.parse(new TextDecoder().decode(bytes)) as TokenPayload;
  } catch {
    return null;
  }
}
